import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Crown, Building2, TrendingUp, DollarSign, Users, Loader2, Edit, ArrowUpRight } from 'lucide-react';
import { toast } from 'sonner';
import { projectId, publicAnonKey } from '../utils/supabase/info';
import { PLAN_DETAILS } from './SubscriptionPlans';
import { LICENSE_MANAGEMENT_ADDONS, formatPrice } from '../utils/pricingConfig';
import { BootstrapSubscriptions } from './BootstrapSubscriptions';
import { CompanyLimitMultiplierSettings } from './CompanyLimitMultiplierSettings';

/** The five plans of the pricing table (utils/pricingConfig), the same ids the server accepts and Stripe sells. */
type SubscriptionPlan = 'free' | 'solo' | 'starter' | 'premium' | 'enterprise';
export const PLAN_IDS: SubscriptionPlan[] = ['free', 'solo', 'starter', 'premium', 'enterprise'];
const planOf = (value: unknown): SubscriptionPlan => (PLAN_IDS.includes(value as SubscriptionPlan) ? (value as SubscriptionPlan) : 'free');
const PLAN_COLORS: Record<SubscriptionPlan, string> = {
  free: 'text-gray-600',
  solo: 'text-slate-600',
  starter: 'text-blue-600',
  premium: 'text-purple-600',
  enterprise: 'text-amber-600',
};
const planPriceLabel = (plan: SubscriptionPlan) => {
  const details = PLAN_DETAILS.find((p) => p.plan === plan);
  return details && details.price > 0 ? `$${details.price.toLocaleString()}/mo` : 'Free';
};

interface SubscriptionManagementProps {
  accessToken: string;
}

interface ConsultingFirmWithSubscription {
  id: string;
  name: string;
  subdomain: string;
  subscriptionPlan: SubscriptionPlan;
  subscriptionStatus?: string;
  billingEmail?: string;
  /** The license management add-on PathKit has enabled for the firm (Firm › Licenses), or null. */
  licenseManagementAddon?: string | null;
  clientCount?: number;
  consultantCount?: number;
}

export function SubscriptionManagement({ accessToken }: SubscriptionManagementProps) {
  const [firms, setFirms] = useState<ConsultingFirmWithSubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedFirm, setSelectedFirm] = useState<ConsultingFirmWithSubscription | null>(null);
  const [updatedPlan, setUpdatedPlan] = useState<SubscriptionPlan>('starter');
  const [billingEmail, setBillingEmail] = useState('');
  const [licenseAddon, setLicenseAddon] = useState<string>('none');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadFirms();
  }, []);

  const loadFirms = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/consulting-firms`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        const firmsData = data.firms || [];
        
        // Fetch subscription details for each firm
        const firmsWithSubscriptions = await Promise.all(
          firmsData.map(async (firm: any) => {
            try {
              const subResponse = await fetch(
                `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/firm/${firm.id}/subscription`,
                {
                  headers: {
                    'Authorization': `Bearer ${accessToken}`,
                  },
                }
              );
              
              if (subResponse.ok) {
                const subData = await subResponse.json();
                return {
                  ...firm,
                  subscriptionPlan: planOf(subData.subscriptionPlan),
                  subscriptionStatus: subData.subscriptionStatus || 'active',
                  billingEmail: subData.billingEmail,
                  licenseManagementAddon: subData.licenseManagementAddon ?? null,
                };
              }
            } catch (error) {
              console.error(`Error fetching subscription for firm ${firm.id}:`, error);
            }
            
            return {
              ...firm,
              subscriptionPlan: planOf(firm.subscriptionPlan),
              subscriptionStatus: 'active'
            };
          })
        );
        
        setFirms(firmsWithSubscriptions);
      } else {
        toast.error('Failed to load consulting firms');
      }
    } catch (error) {
      console.error('Error loading firms:', error);
      toast.error('Error loading consulting firms');
    } finally {
      setLoading(false);
    }
  };

  const handleEditSubscription = (firm: ConsultingFirmWithSubscription) => {
    setSelectedFirm(firm);
    setUpdatedPlan(firm.subscriptionPlan);
    setBillingEmail(firm.billingEmail || '');
    setLicenseAddon(firm.licenseManagementAddon || 'none');
    setEditDialogOpen(true);
  };

  const handleSaveSubscription = async () => {
    if (!selectedFirm) return;

    setSaving(true);
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/firm/${selectedFirm.id}/subscription`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            subscriptionPlan: updatedPlan,
            billingEmail: billingEmail || undefined,
            subscriptionStatus: 'active'
          }),
        }
      );

      // The license management add-on is granted here, by PathKit (the firm cannot switch it on itself)
      const addonId = licenseAddon === 'none' ? null : licenseAddon;
      if (response.ok && addonId !== (selectedFirm.licenseManagementAddon ?? null)) {
        const addonResponse = await fetch(
          `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/license/subscribe`,
          {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ firmId: selectedFirm.id, addonId }),
          }
        );
        if (!addonResponse.ok) {
          const err = await addonResponse.json().catch(() => ({}));
          toast.error(err.error || 'Plan saved, but the license add-on could not be changed');
        }
      }

      if (response.ok) {
        toast.success(`Updated subscription plan for ${selectedFirm.name}`);
        setEditDialogOpen(false);
        loadFirms(); // Reload to show updated data
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || 'Failed to update subscription');
      }
    } catch (error) {
      console.error('Error updating subscription:', error);
      toast.error('Error updating subscription');
    } finally {
      setSaving(false);
    }
  };

  const getPlanColor = (plan: SubscriptionPlan) => PLAN_COLORS[plan] ?? 'text-gray-600';

  const calculateMRR = () => {
    return firms.reduce((total, firm) => {
      const planDetails = PLAN_DETAILS.find(p => p.plan === firm.subscriptionPlan);
      return total + (planDetails?.price || 0);
    }, 0);
  };

  const calculateARR = () => {
    return calculateMRR() * 12;
  };

  const getPlanCounts = () => {
    const counts: Record<SubscriptionPlan, number> = { free: 0, solo: 0, starter: 0, premium: 0, enterprise: 0 };
    
    firms.forEach(firm => {
      counts[firm.subscriptionPlan] = (counts[firm.subscriptionPlan] || 0) + 1;
    });
    
    return counts;
  };

  const planCounts = getPlanCounts();

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="mb-2">Subscription Management</h2>
        <p className="text-muted-foreground">
          Manage subscription plans and billing for all consulting firms
        </p>
      </div>

      {/* Global company-limit multiplier (per-firm overrides live in Consulting Firms) */}
      <CompanyLimitMultiplierSettings accessToken={accessToken} />

      {/* Revenue Metrics */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Monthly Revenue</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${calculateMRR().toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              MRR from {firms.length} firms
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Annual Revenue</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${calculateARR().toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              ARR projection
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Firms</CardTitle>
            <Building2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{firms.length}</div>
            <p className="text-xs text-muted-foreground">
              Active consulting firms
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg. Revenue</CardTitle>
            <ArrowUpRight className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              ${firms.length > 0 ? Math.round(calculateMRR() / firms.length) : 0}
            </div>
            <p className="text-xs text-muted-foreground">
              Per firm / month
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Plan Distribution */}
      <Card>
        <CardHeader>
          <CardTitle>Plan Distribution</CardTitle>
          <CardDescription>
            Number of firms on each subscription tier
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-5" data-testid="plan-distribution">
            {PLAN_IDS.map((plan) => (
              <div key={plan} className="flex items-center gap-3 p-3 border rounded-lg">
                <div className={`text-2xl ${getPlanColor(plan)}`}>{planCounts[plan]}</div>
                <div>
                  <p>{PLAN_DETAILS.find((p) => p.plan === plan)?.displayName ?? plan}</p>
                  <p className="text-muted-foreground">{planPriceLabel(plan)}</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Firms Table */}
      <Card>
        <CardHeader>
          <CardTitle>Consulting Firms</CardTitle>
          <CardDescription>
            Manage subscription plans for each consulting firm
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Firm Name</TableHead>
                <TableHead>Subdomain</TableHead>
                <TableHead>Current Plan</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Billing Email</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {firms.map((firm) => (
                <TableRow key={firm.id}>
                  <TableCell className="font-medium">{firm.name}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{firm.subdomain}</Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Crown className={`h-4 w-4 ${getPlanColor(firm.subscriptionPlan)}`} />
                      <span className="capitalize">{firm.subscriptionPlan}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={firm.subscriptionStatus === 'active' ? 'default' : 'secondary'}>
                      {firm.subscriptionStatus || 'active'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {firm.billingEmail || '—'}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEditSubscription(firm)}
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Edit Subscription Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Subscription Plan</DialogTitle>
            <DialogDescription>
              Update the subscription plan for {selectedFirm?.name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="plan">Subscription Plan</Label>
              <Select value={updatedPlan} onValueChange={(value) => setUpdatedPlan(value as SubscriptionPlan)}>
                <SelectTrigger id="plan">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PLAN_IDS.map((plan) => (
                    <SelectItem key={plan} value={plan}>
                      <div className="flex items-center gap-2">
                        <Crown className={`h-4 w-4 ${getPlanColor(plan)}`} />
                        <span>{PLAN_DETAILS.find((p) => p.plan === plan)?.displayName ?? plan} - {planPriceLabel(plan)}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="licenseAddon">License management add-on</Label>
              <Select value={licenseAddon} onValueChange={setLicenseAddon}>
                <SelectTrigger id="licenseAddon">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {Object.values(LICENSE_MANAGEMENT_ADDONS).map((addon) => (
                    <SelectItem key={addon.id} value={addon.id}>
                      {addon.displayName} - {formatPrice(addon.price)}/mo
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-muted-foreground mt-1">Turns on Firm › Licenses for this firm.</p>
            </div>

            <div>
              <Label htmlFor="billingEmail">Billing Email (Optional)</Label>
              <Input
                id="billingEmail"
                type="email"
                value={billingEmail}
                onChange={(e) => setBillingEmail(e.target.value)}
                placeholder="billing@example.com"
              />
            </div>

            <div className="p-3 border rounded-lg bg-blue-50 dark:bg-blue-950">
              <p className="text-sm">
                <strong>Plan Details:</strong> {PLAN_DETAILS.find(p => p.plan === updatedPlan)?.displayName || 'Unknown Plan'}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {PLAN_DETAILS.find(p => p.plan === updatedPlan)?.features?.slice(0, 3).join(' • ') || 'No features available'}
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSaveSubscription} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bootstrap Subscriptions (for testing/migration) */}
      <BootstrapSubscriptions accessToken={accessToken} />
    </div>
  );
}